import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useLocation,
  useNavigate,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { ThemeProvider, useTheme } from "@/hooks/use-theme";
import { Toaster } from "@/components/ui/sonner";
import { UpgradeSheet } from "@/components/upgrade-sheet";
import { ConsentBanner } from "@/components/consent-banner";
import { BootSplash } from "@/components/boot-splash";
import { isChunkLoadError } from "@/lib/error-capture";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  const chunkError = isChunkLoadError(error);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {chunkError
            ? "A new version of the app is available. Reload to continue."
            : "Something went wrong on our end. You can try refreshing or head back home."}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              if (chunkError && typeof window !== "undefined") {
                window.location.reload();
                return;
              }
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {chunkError ? "Reload app" : "Try again"}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover" },
      { title: "Pyng — Know exactly who to reach out to today" },
      { name: "description", content: "A private watchlist for the people who shape your career. Pyng watches them around the clock and hands you the message, ready to send. Private by default — never shared or sold." },
      { name: "theme-color", content: "#090A0C" },
      { property: "og:title", content: "Pyng — Know exactly who to reach out to today" },
      { property: "og:description", content: "A private watchlist for the people who shape your career. Pyng watches them around the clock and hands you the message, ready to send. Private by default — never shared or sold." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Pyng — Know exactly who to reach out to today" },
      { name: "twitter:description", content: "A private watchlist for the people who shape your career. Pyng watches them around the clock and hands you the message, ready to send. Private by default — never shared or sold." },
      { property: "og:image", content: "https://pyng.co/og-image.png?v=5" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Pyng — know exactly who to reach out to today. Private by default." },
      { name: "twitter:image", content: "https://pyng.co/og-image.png?v=5" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head suppressHydrationWarning>
        <HeadContent />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){function fmt(e){try{return (e&&e.stack)?e.stack:String(e);}catch(_){return 'unknown';}}window.addEventListener('error',function(ev){try{console.error('[STARTUP onerror]',ev.message,'@',ev.filename+':'+ev.lineno+':'+ev.colno,'\\n',fmt(ev.error));}catch(_){}});window.addEventListener('unhandledrejection',function(ev){try{console.error('[STARTUP unhandledrejection]',fmt(ev.reason));}catch(_){}});})();`,
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var t=localStorage.getItem('aria:theme')||'dark';var m=window.matchMedia('(prefers-color-scheme: light)').matches;var r=t==='system'?(m?'light':'dark'):(t==='light'?'light':'dark');document.documentElement.classList.add(r);var mt=document.querySelector('meta[name=theme-color]');if(mt)mt.setAttribute('content',r==='light'?'#F4F2ED':'#090A0C');}catch(e){document.documentElement.classList.add('dark');}})();`,
          }}
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <PostAuthRedirect />
          <Outlet />
          <BootSplash />
          <ThemedToaster />
          <ConsentBanner />
          <UpgradeSheet />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function PostAuthRedirect() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || !user) return;

    const pendingPath = sessionStorage.getItem("orbit:post-auth-path");
    const safePendingPath = pendingPath === "/today" ? "/today" : null;
    const isAuthReturn = location.pathname === "/" || location.pathname === "/login";
    const destination = safePendingPath ?? (isAuthReturn ? "/today" : null);

    sessionStorage.removeItem("orbit:post-auth-path");
    if (!destination || location.pathname === destination) return;

    void navigate({ to: "/today", replace: true });
  }, [loading, user, location.pathname, navigate]);

  return null;
}

function ThemedToaster() {
  const { resolvedTheme } = useTheme();
  return <Toaster theme={resolvedTheme} position="top-center" />;
}
